// Cross-browser display-placement detection. Pure browser helpers, no React,
// no imports — the geometry half is a pure function so it unit-tests without
// jsdom (same convention as `offscreenFraction` in ./index.ts).
//
// WHY THIS EXISTS
// `screen.isExtended` — the only API that can answer "is a second display
// connected?" — is Chromium-only over a secure context. Firefox and Safari
// expose no multi-display API at all (verified against MDN, July 2026), and
// even Chromium reports `undefined` on a non-secure origin. Until 2026-08-24
// those browsers fell back to a window-geometry heuristic that is a *proxy*
// (how far off the primary screen is the window?) for a *fact* (which display
// is the window on?), and it silently missed a whole common desk layout — see
// the note on `offscreenFraction` in ./index.ts.
//
// `screen.availLeft` / `availTop` are non-standard but implemented on the
// `Screen` interface in every browser, and report the CURRENT display's
// available-area origin in the OS virtual-screen coordinate space. The primary
// display's origin is (0,0) by definition on both Windows and macOS, so a
// non-zero origin means the window is on a different display — which means a
// second display exists. That is a fact, not a proxy, and it works on Firefox
// and Safari.
//
// WHAT THIS STILL CANNOT SEE, on any browser without `isExtended`: a second
// monitor the test window is never moved onto. That gap is real and is why the
// recruiter-facing capability state is "limited" rather than "none detected" —
// see backend/risk.py `_SECOND_SCREEN_RANK`.

/** Minimum virtual-coordinate offset that counts as "a different display".
 *
 * Any real second display sits at least one display width/height away from the
 * origin — 640 CSS px even for the smallest plausible panel. 100 px is far
 * above any OS-chrome reporting inconsistency and far below any real display
 * offset, so it separates the two populations with a wide margin. */
export const MIN_DISPLAY_OFFSET_PX = 100;

export type DisplayPlacement = "primary" | "non_primary" | "unknown";

export interface ScreenMetrics {
  width: number;
  height: number;
  availWidth: number;
  availHeight: number;
  availLeft: number;
  availTop: number;
}

/** Is this window on a display whose virtual origin is NOT (0,0)?
 *
 * PURE — no DOM reads, so it unit-tests without jsdom.
 *
 * The predicate exploits a self-normalising invariant: on the PRIMARY display
 * the available area is a sub-rectangle of a display whose origin is (0,0), so
 * `availLeft` can never exceed the OS chrome drawn on that same display, which
 * is exactly `width - availWidth`. Subtracting the chrome makes a left-docked
 * Windows taskbar (availLeft 60, chromeX 60) and a macOS Dock (availLeft 200,
 * chromeX 200) both cancel to 0, while a genuine second display still reads its
 * full virtual offset.
 *
 * Zoom- and DPI-invariant BY CONSTRUCTION, which is load-bearing: Firefox
 * scales `screen.width` with full-page zoom and Chromium does not. All six
 * values come from the same `Screen` object in the same unit, so any scale
 * factor multiplies both sides of every comparison and cancels. Do NOT
 * "normalise" this with devicePixelRatio — that would cancel zoom on one
 * engine and INTRODUCE it on the other.
 *
 * Fails safe: anything unreadable or self-contradictory returns "unknown", and
 * a browser that spoofs the screen (Firefox `privacy.resistFingerprinting`
 * reports all-zeros) reads "primary" — a miss, never a false accusation.
 */
export function displayPlacement(m: ScreenMetrics): DisplayPlacement {
  const { width, height, availWidth, availHeight, availLeft, availTop } = m;
  // An absent non-standard property arrives as `undefined` -> NaN here.
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(availWidth) ||
    !Number.isFinite(availHeight) ||
    !Number.isFinite(availLeft) ||
    !Number.isFinite(availTop)
  ) {
    return "unknown";
  }
  if (!(width > 0) || !(height > 0)) return "unknown";
  if (!(availWidth > 0) || !(availHeight > 0)) return "unknown";
  // Self-inconsistent report — the available area cannot exceed the screen.
  if (availWidth > width || availHeight > height) return "unknown";

  // Displays left of / above the primary sit at negative virtual coordinates,
  // which no amount of OS chrome can produce on the primary itself.
  if (availLeft < -MIN_DISPLAY_OFFSET_PX) return "non_primary";
  if (availTop < -MIN_DISPLAY_OFFSET_PX) return "non_primary";

  // Displays right of / below the primary sit at a positive offset larger than
  // any chrome drawn on their own display.
  const chromeX = width - availWidth;
  const chromeY = height - availHeight;
  if (availLeft - chromeX > MIN_DISPLAY_OFFSET_PX) return "non_primary";
  if (availTop - chromeY > MIN_DISPLAY_OFFSET_PX) return "non_primary";

  return "primary";
}

/** Read the live `Screen` metrics. An absent non-standard property yields NaN,
 * which `displayPlacement` maps to "unknown". */
export function readScreenMetrics(): ScreenMetrics {
  const scr = (typeof window !== "undefined" ? window.screen : undefined) as
    | (Screen & { availLeft?: number; availTop?: number })
    | undefined;
  const num = (v: unknown): number => (typeof v === "number" ? v : NaN);
  return {
    width: num(scr?.width),
    height: num(scr?.height),
    availWidth: num(scr?.availWidth),
    availHeight: num(scr?.availHeight),
    availLeft: num(scr?.availLeft),
    availTop: num(scr?.availTop),
  };
}

/** DOM wrapper around `displayPlacement`.
 *
 * The `document.hidden` gate mirrors `windowOnSecondaryScreen()`: a window
 * nobody can see must never count as being on a second display. It returns
 * "unknown" rather than "primary" so a hidden window is never mistaken for
 * evidence that the candidate is on one screen.
 */
export function probeDisplayPlacement(): DisplayPlacement {
  if (typeof document !== "undefined" && document.hidden) return "unknown";
  return displayPlacement(readScreenMetrics());
}

/** Whether this browser can run the indirect placement check at all.
 *
 * Drives the "limited" vs "unsupported" capability split the recruiter sees:
 * "limited" means we checked continuously and saw nothing; "unsupported" means
 * nothing could be checked. Reads the live screen, so it must NOT inherit the
 * `document.hidden` gate — capability is a property of the browser, not of
 * whether the tab happens to be visible at probe time. */
export function canProbeDisplayPlacement(): boolean {
  return displayPlacement(readScreenMetrics()) !== "unknown";
}

/** Device form factor, for the recruiter caveat.
 *
 * A touch-only device has no element fullscreen on iOS Safari and no fine
 * pointer, so fullscreen enforcement, cursor tracking and the second-screen
 * check are ALL structurally inert — three zeros that must not read as good
 * behaviour. Detected from capability, not the user-agent string: iPadOS
 * Safari reports a desktop UA but still reports `maxTouchPoints === 5`.
 * PURE. */
export function isTouchOnlyDevice(env: {
  maxTouchPoints: number;
  anyPointerFine: boolean;
}): boolean {
  return env.maxTouchPoints > 0 && !env.anyPointerFine;
}

/** DOM wrapper around `isTouchOnlyDevice`. A browser without matchMedia is
 * treated as a plain desktop (preserves the status quo rather than inventing a
 * caveat) — same default as `probeCursorTracking`. */
export function probeFormFactor(): "desktop" | "touch" {
  try {
    if (typeof window === "undefined") return "desktop";
    const anyPointerFine =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(any-pointer: fine)").matches
        : true;
    return isTouchOnlyDevice({
      maxTouchPoints: navigator.maxTouchPoints ?? 0,
      anyPointerFine,
    })
      ? "touch"
      : "desktop";
  } catch {
    return "desktop";
  }
}
